import {
  ApplicationRef,
    ChangeDetectorRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  TemplateRef
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';
import { InvokeType, PositionState, TooltipTheme } from './tooltip.config';

@Directive({
  selector: '[appTooltip]',
  exportAs: 'app-tooltip'
})
export class TooltipDirective implements OnDestroy, OnChanges {
  @Input() appTooltip: string | TemplateRef<unknown>;
  @Input() tooltipIndent = 15;
  @Input() tooltipInvokeType: InvokeType | string = InvokeType.hover;
  @Input() tooltipPosition: PositionState | string = PositionState.top;
  @Input() tooltipCustomClass: string;
  @Input() tooltipMaxWidth: number;
  @Input() tooltipTheme: string | TooltipTheme = TooltipTheme.black;
  @Input() tooltipIsDisabled = false;
  @Input() tooltipHasBeak = true;

  @Output() onHidden: EventEmitter<void> = new EventEmitter();

  opened = false;
  closedFromComponent = false;

  private componentRef: ComponentRef<any>;

  constructor(protected elementRef: ElementRef,
              private componentFactoryResolver: ComponentFactoryResolver,
              private injector: Injector,
              private appRef: ApplicationRef,
              private cdRef: ChangeDetectorRef) {
  }

  ngOnChanges() {
    if (!this.tooltipIsDisabled && this.appTooltip) {
      const el = this.elementRef.nativeElement as HTMLElement;
      if (this.tooltipInvokeType === InvokeType.hover) {
        el.addEventListener('mouseenter', () => this.show());
        el.addEventListener('mouseleave', () => this.hide());
        el.addEventListener('click', () => this.hide());
      } else {
        el.addEventListener('click', () => {
          if (!this.componentRef && !this.closedFromComponent) {
            this.show();
          } else {
            this.hide();
            this.closedFromComponent = false;
          }
        });
      }
    }
  }

  show() {
    if (!this.componentRef) {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(TooltipComponent);
      this.componentRef = componentFactory.create(this.injector);
      this.appRef.attachView(this.componentRef.hostView);
      const domElement = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElement);
      this.setupTooltipPropwerties();
      this.opened = true;
      this.cdRef.detectChanges();
    }
  }

  private setupTooltipPropwerties() {
    if (this.componentRef) {
      this.setPositionOnView();
      this.componentRef.instance.content = this.appTooltip;
      this.componentRef.instance.position = this.tooltipPosition;
      this.componentRef.instance.class = this.tooltipCustomClass;
      this.componentRef.instance.maxWidth = this.tooltipMaxWidth;
      this.componentRef.instance.theme = this.tooltipTheme;
      this.componentRef.instance.hasBeak = this.tooltipHasBeak;
    }
  }

  private setPositionOnView() {
    const el = this.elementRef.nativeElement as HTMLDivElement;
    const { left, right, bottom, top } = el.getBoundingClientRect();
    this.componentRef.instance.left = this.setLeftPosition(left, right);
    this.componentRef.instance.top = this.setTopPosition(top, bottom);
  }

  private setLeftPosition(left: number, right: number): number {
    if (this.tooltipPosition === PositionState.top || this.tooltipPosition === PositionState.bottom) {
      return (right - left) / 2 + left;
    }
    if (this.tooltipPosition === PositionState.topRight || this.tooltipPosition === PositionState.bottomRight) {
      return right;
    }
    if (this.tooltipPosition === PositionState.topLeft || this.tooltipPosition === PositionState.bottomLeft) {
      return left;
    }
    if (this.tooltipPosition === PositionState.left || this.tooltipPosition === PositionState.leftTop
      || this.tooltipPosition === PositionState.leftBottom) {
      return left - this.tooltipIndent;
    }
    if (this.tooltipPosition === PositionState.right || this.tooltipPosition === PositionState.rightTop ||
      this.tooltipPosition === PositionState.rightBottom) {
      return right + this.tooltipIndent;
    }

  }

  private setTopPosition(top: number, bottom: number): number {
    if (this.tooltipPosition === PositionState.top ||
      this.tooltipPosition === PositionState.topLeft ||
      this.tooltipPosition === PositionState.topRight) {
      return top - this.tooltipIndent;
    }
    if (this.tooltipPosition === PositionState.bottom ||
      this.tooltipPosition === PositionState.bottomLeft ||
      this.tooltipPosition === PositionState.bottomRight) {
      return bottom + this.tooltipIndent;
    }
    if (this.tooltipPosition === PositionState.left || this.tooltipPosition === PositionState.right ||
      this.tooltipPosition === PositionState.leftTop || this.tooltipPosition === PositionState.leftBottom ||
      this.tooltipPosition === PositionState.rightTop || this.tooltipPosition === PositionState.rightBottom) {
      return (bottom - top) / 2 + top;
    }
  }

  hide() {
    if (this.componentRef) {
      this.onHidden.emit();
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
      this.opened = false;
      this.cdRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.hide();
  }
}
